import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { CurrencyService, CurrencyInfo } from './currency.service';
import { VendorQuotationCurrencyContextService } from './vendor-quotation-currency-context.service';
import { SocketService } from './socket.service';

/**
 * Listens to `bidUpdate` socket stream app-wide so bid accept/reject toasts appear on any route
 * (Dashboard, Orders, etc.), not only inside event detail / quotation.
 */
@Injectable({ providedIn: 'root' })
export class VendorBidSocketToastService {
    private readonly socketService = inject(SocketService);
    private readonly toastr = inject(ToastrService);
    private readonly currencyService = inject(CurrencyService);
    private readonly quotationCurrencyContext = inject(VendorQuotationCurrencyContextService);
    private readonly destroyRef = inject(DestroyRef);

    constructor() {
        this.socketService.socketBidData
            .pipe(filter((d: any) => d != null), takeUntilDestroyed(this.destroyRef))
            .subscribe((data) => this.handleBidPayload(data));
    }

    private getVendorDataFromSession(): any {
        const vendorData = localStorage.getItem('VendorData');
        if (vendorData) {
            try {
                return JSON.parse(vendorData);
            } catch {
                return null;
            }
        }
        return null;
    }

    private eventIdFromPayload(socketPayload: any): string | null {
        const id =
            socketPayload?.data?.eventData?.uuid ??
            socketPayload?.eventData?.uuid ??
            socketPayload?.eventId ??
            socketPayload?.data?.eventId ??
            socketPayload?.data?.eventData?.id ??
            socketPayload?.eventData?.id;
        return id != null && String(id).trim() !== '' ? String(id) : null;
    }

    private currencyInfoFromPayload(socketPayload: any, bidData: any): CurrencyInfo | null {
        const c =
            bidData?.currency ??
            socketPayload?.data?.eventData?.currency ??
            socketPayload?.eventData?.currency;
        if (c) {
            if (typeof c === 'string') {
                const code = c.trim().toUpperCase();
                return {
                    code,
                    symbol: this.currencyService.getCurrencySymbol(code),
                    name: code,
                };
            }
            return this.currencyService.extractCurrencyFromApi(c);
        }

        const fromQuotationTab = this.quotationCurrencyContext.getEventDisplayCurrency(
            this.eventIdFromPayload(socketPayload)
        );
        if (fromQuotationTab?.code) {
            return fromQuotationTab;
        }

        const vendorCurrency =
            socketPayload?.data?.eventData?.event_vendors?.[0]?.currency ??
            socketPayload?.eventData?.event_vendors?.[0]?.currency;
        const vendorCode = vendorCurrency?.currency;
        if (vendorCode != null && String(vendorCode).trim() !== '') {
            const code = String(vendorCode).trim().toUpperCase();
            return {
                code,
                symbol: this.currencyService.getCurrencySymbol(code),
                name: code,
            };
        }

        const bidCurrencyCode = bidData?.currencyCode ?? bidData?.currency_code;
        if (bidCurrencyCode != null && String(bidCurrencyCode).trim() !== '') {
            const code = String(bidCurrencyCode).trim().toUpperCase();
            return {
                code,
                symbol: this.currencyService.getCurrencySymbol(code),
                name: code,
            };
        }

        return null;
    }

    private formatAmount(socketPayload: any, bidData: any, amount: string | number): string {
        const currency = this.currencyInfoFromPayload(socketPayload, bidData);
        return this.currencyService.formatCurrencyCompact(amount, currency, { skipConversion: true });
    }

    private handleBidPayload(socketPayload: any): void {
        const t = socketPayload?.type ?? socketPayload?.data?.type;
        if (t !== 'BID_ACCEPTED' && t !== 'BID_REJECTED') {
            return;
        }
        this.showBidAcceptedToastIfSessionVendor(socketPayload);
        this.showBidRejectedToastIfSessionVendor(socketPayload);
        // Defer clear so other subscribers (event list, event shell) still see this emission in the same tick.
        setTimeout(() => this.socketService.clearNotificationData('bid'), 0);
    }

    private showBidAcceptedToastIfSessionVendor(socketPayload: any): void {
        const t = socketPayload?.type ?? socketPayload?.data?.type;
        if (t !== 'BID_ACCEPTED') {
            return;
        }
        const bidData = socketPayload?.data?.bidData ?? socketPayload?.bidData;
        const bidVendorId = bidData?.vendorId;
        const sessionVendor = this.getVendorDataFromSession();
        const myVendorId = sessionVendor?.vendor_id ?? sessionVendor?.id;
        if (bidVendorId == null || myVendorId == null) {
            return;
        }
        if (String(bidVendorId) !== String(myVendorId)) {
            return;
        }
        const amountText = this.formatAmount(socketPayload, bidData, bidData?.amount ?? 0);
        const eventNameRaw =
            socketPayload?.data?.eventData?.name ??
            socketPayload?.eventData?.name ??
            '';
        const eventLabel = String(eventNameRaw ?? '').trim() || 'event';
        const partNameText = String(bidData?.partName ?? '').trim();
        const partSegment = partNameText ? ` for part "${partNameText}"` : '';
        const message = `Your quote of ${amountText}${partSegment} for event ${eventLabel} has been accepted.`;
        this.toastr.info(message, 'Bid Accepted');
    }

    private showBidRejectedToastIfSessionVendor(socketPayload: any): void {
        const t = socketPayload?.type ?? socketPayload?.data?.type;
        if (t !== 'BID_REJECTED') {
            return;
        }
        const bidData = socketPayload?.data?.bidData ?? socketPayload?.bidData;
        const bidVendorId = bidData?.vendorId;
        const sessionVendor = this.getVendorDataFromSession();
        const myVendorId = sessionVendor?.vendor_id ?? sessionVendor?.id;
        if (bidVendorId == null || myVendorId == null) {
            return;
        }
        if (String(bidVendorId) !== String(myVendorId)) {
            return;
        }
        const amountText = this.formatAmount(socketPayload, bidData, bidData?.amount ?? 0);
        const eventNameRaw =
            socketPayload?.data?.eventData?.name ??
            socketPayload?.eventData?.name ??
            '';
        const eventLabel = String(eventNameRaw ?? '').trim() || 'event';
        const partNameText = String(bidData?.partName ?? '').trim();
        const partSegment = partNameText ? ` for part "${partNameText}"` : '';
        const lastQuoteRejectedMessage = `Your quote of ${amountText}${partSegment} for event ${eventLabel} has been rejected.`;

        const status = String(bidData?.status ?? bidData?.bidStatus ?? '')
            .trim()
            .toUpperCase();

        let message = lastQuoteRejectedMessage;
        let title = 'Bid Rejected';

        if (status === 'BLOCKED' || status === 'REJECTED') {
            message = `You have been blocked for ${eventLabel}.`;
            title = 'Blocked';
        } else if (status === 'COMPLETE_REJECTED') {
            message = partNameText
                ? `All your quotes for ${partNameText} of ${eventLabel} have been rejected.`
                : `All your quotes for ${eventLabel} have been rejected.`;
            title = 'Complete Bid Rejected';
        }

        this.toastr.info(message, title);
    }
}
